import type {MaskitoOptions} from "@maskito/core";
import {maskitoDateOptionsGenerator} from "@maskito/kit";
import {useMaskito} from "@maskito/react";
import {TextField, type TextFieldProps} from "@mui/material";
import type React from "react";
import {type Control, type FieldPath, type FieldValues, useController} from "react-hook-form";

export type DateOfBirthFieldProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    // biome-ignore lint/suspicious/noExplicitAny: defined by RHF
    TContext = any,
    TTransformedValues = TFieldValues,
> = Omit<TextFieldProps, "error" | "onChange" | "onBlur" | "value" | "inputRef"> & {
    control: Control<TFieldValues, TContext, TTransformedValues>;
    name: TName;
};

const dobMask: MaskitoOptions = maskitoDateOptionsGenerator({
    mode: "mm/dd/yyyy",
    separator: "/",
});

const DateOfBirthField = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    control,
    name,
    ...props
}: DateOfBirthFieldProps<TFieldValues, TName>): React.ReactElement => {
    const inputRef = useMaskito({options: dobMask});
    const {field, fieldState} = useController({control, name});

    return (
        <TextField
            inputRef={inputRef}
            name={name}
            onBlur={field.onBlur}
            onInput={field.onChange}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message ?? ""}
            {...props}
            value={field.value ?? ""}
        />
    );
};

export default DateOfBirthField;
